/**
 * ตัดสินธีม UI (classic ⇄ glass) — logic ล้วน ไม่แตะ DOM (ทดสอบด้วย node --test)
 * ลำดับ: ?ui= (ทดสอบ, ไม่บันทึก) > Firestore publicIntegrations/ordercusUi (แอดมิน) > localStorage > classic
 * inline script ใน index.html ทำ resolveInitialUiTheme ซ้ำแบบ ES5 — แก้ที่หนึ่งต้องแก้อีกที่
 */
export type UiTheme = 'classic' | 'glass'

export const UI_THEME_STORAGE_KEY = 'oc-ui'

export function parseUiTheme(v: unknown): UiTheme | null {
  return v === 'classic' || v === 'glass' ? v : null
}

export function readUrlUiTheme(search: string): UiTheme | null {
  try {
    const sp = new URLSearchParams(search)
    const direct = parseUiTheme(sp.get('ui'))
    if (direct) return direct
    let state = sp.get('liff.state')
    if (!state) return null
    // LINE บางทางส่ง liff.state เข้ารหัสซ้อน 2 ชั้น — ยังเหลือ %3F/%3D หลัง get → ถอดอีกครั้ง
    if (/%3[fd]/i.test(state)) state = decodeURIComponent(state)
    if (state.startsWith('?')) state = state.slice(1)
    return parseUiTheme(new URLSearchParams(state).get('ui'))
  } catch {
    return null
  }
}

export function resolveInitialUiTheme(urlTheme: UiTheme | null, stored: unknown): UiTheme {
  return urlTheme ?? parseUiTheme(stored) ?? 'classic'
}

export function decideRemoteApply(input: {
  urlTheme: UiTheme | null
  current: UiTheme
  remote: UiTheme
  orderVisible: boolean
}): { apply: UiTheme | null; store: UiTheme | null } {
  if (input.urlTheme) return { apply: null, store: null }
  const apply = !input.orderVisible && input.remote !== input.current ? input.remote : null
  return { apply, store: input.remote }
}

/**
 * ชุดสีของธีม glass (12 ต.ค. 2569) — แยกจาก theme เพื่อให้ OrderCus เวอร์ชันเก่ายังอ่าน theme='glass' ได้ตามเดิม
 * Firestore `publicIntegrations/ordercusUi.palette` · 'royal' = ม่วง-ทองเดิม (ค่าเริ่มต้น) · 'smoky' = Smoky Warm Glass · 'ground' = พื้นหลังไหล (groundCore.ts)
 * ลำดับ: ?tone= (ทดสอบ, ไม่บันทึก) > Firestore > localStorage > royal · inline script ใน index.html ทำซ้ำแบบ ES5
 */
export type UiPalette = 'royal' | 'smoky' | 'ground'

export const UI_PALETTE_STORAGE_KEY = 'oc-tone'

export function parseUiPalette(v: unknown): UiPalette | null {
  return v === 'royal' || v === 'smoky' || v === 'ground' ? v : null
}

export function readUrlUiPalette(search: string): UiPalette | null {
  try {
    const sp = new URLSearchParams(search)
    const direct = parseUiPalette(sp.get('tone'))
    if (direct) return direct
    let state = sp.get('liff.state')
    if (!state) return null
    if (/%3[fd]/i.test(state)) state = decodeURIComponent(state)
    if (state.startsWith('?')) state = state.slice(1)
    return parseUiPalette(new URLSearchParams(state).get('tone'))
  } catch {
    return null
  }
}

/** อ่าน palette จาก response Firestore REST (fields.palette.stringValue) — ไม่มี/ผิดรูป = royal */
/** response REST ของ ordercusUi → ธีม · ไม่มี/ผิดรูป = null (คงธีมเดิม) */
export function themeFromRestDoc(doc: unknown): UiTheme | null {
  const fields = (doc as { fields?: { theme?: { stringValue?: unknown } } } | null)?.fields
  return parseUiTheme(fields?.theme?.stringValue)
}

export function paletteFromRestDoc(doc: unknown): UiPalette {
  const fields = (doc as { fields?: { palette?: { stringValue?: unknown } } } | null)?.fields
  return parseUiPalette(fields?.palette?.stringValue) ?? 'royal'
}

/**
 * โหมดประหยัดพลังงาน (13 ต.ค. 2569) — แอดมินตั้งที่ admin.srikanett.com › สมาชิก › หน้าลูกค้า › ธีม
 * Firestore `publicIntegrations/ordercusUi.energy` · 'auto' (ค่าเริ่มต้น, ตามเครื่อง) | 'on' (ประหยัดทุกเครื่อง) | 'off' (เอฟเฟกต์เต็ม)
 * ลำดับ: ?energy= (ทดสอบ, ไม่บันทึก) > Firestore > localStorage > auto · inline script ใน index.html ทำซ้ำแบบ ES5
 * ผลเมื่อประหยัด: <html data-energy="on"> → CSS (@layer oc-energy ท้าย globals.css) ปิดเบลอ/แอนิเมชันตกแต่ง + หยุดวิดีโอ
 */
export type EnergyMode = 'auto' | 'on' | 'off'

export const UI_ENERGY_STORAGE_KEY = 'oc-energy'

export function parseEnergyMode(v: unknown): EnergyMode | null {
  return v === 'auto' || v === 'on' || v === 'off' ? v : null
}

export function readUrlEnergyMode(search: string): EnergyMode | null {
  try {
    const sp = new URLSearchParams(search)
    const direct = parseEnergyMode(sp.get('energy'))
    if (direct) return direct
    let state = sp.get('liff.state')
    if (!state) return null
    if (/%3[fd]/i.test(state)) state = decodeURIComponent(state)
    if (state.startsWith('?')) state = state.slice(1)
    return parseEnergyMode(new URLSearchParams(state).get('energy'))
  } catch {
    return null
  }
}

/** อ่าน energy จาก response Firestore REST (fields.energy.stringValue) — ไม่มี/ผิดรูป = auto */
export function energyFromRestDoc(doc: unknown): EnergyMode {
  const fields = (doc as { fields?: { energy?: { stringValue?: unknown } } } | null)?.fields
  return parseEnergyMode(fields?.energy?.stringValue) ?? 'auto'
}

export type EnergyEnv = {
  reducedMotion: boolean
  saveData: boolean
  /** hardwareConcurrency ≤ 4 หรือ deviceMemory ≤ 4 */
  lowHardware: boolean
  /** แบตต่ำกว่า 22% และไม่ได้ชาร์จ */
  lowBattery: boolean
}

/** 'on' / 'off' ชนะเสมอ · 'auto' = ประหยัดเมื่อเครื่องบอกว่าควรประหยัด */
export function resolveEnergyActive(mode: EnergyMode, env: EnergyEnv): boolean {
  if (mode === 'on') return true
  if (mode === 'off') return false
  return env.reducedMotion || env.saveData || env.lowHardware || env.lowBattery
}


/**
 * ฉากหลังองค์พ่อคู่พระแม่เบลอ (13 ต.ค. 2569) — แอดมินตั้งที่ สมาชิก › หน้าลูกค้า › ธีม › "ฉากหลังองค์พ่อคู่พระแม่"
 * Firestore `publicIntegrations/ordercusUi.scene` = { enabled, loading, member, order, blur, dim }
 * ไม่มีค่า/ผิดรูป = เปิดทุกหน้า ความเบลอกลาง (พฤติกรรมเดิมก่อนมีตัวตั้งค่า) · ?scene=on|off ทดสอบ ไม่บันทึก
 * ผล: <html data-scene="off"> ซ่อนภาพทุกหน้า · data-scene-{loading|member|order}="off" ซ่อนรายหน้า · data-scene-blur="soft|sharp"
 * · data-scene-dim="light|dark" (ความมืดของภาพ — 22 ต.ค. 2569 · ไม่มีค่า = กลาง = หน้าตาเดิม)
 */
export type SceneBlur = 'soft' | 'mid' | 'sharp'
export type SceneDim = 'light' | 'mid' | 'dark'

export interface SceneConfig {
  enabled: boolean
  loading: boolean
  member: boolean
  order: boolean
  blur: SceneBlur
  dim: SceneDim
}

export const SCENE_DEFAULT: SceneConfig = { enabled: true, loading: true, member: true, order: true, blur: 'mid', dim: 'mid' }
export const UI_SCENE_STORAGE_KEY = 'oc-scene'

export function parseSceneBlur(v: unknown): SceneBlur | null {
  return v === 'soft' || v === 'mid' || v === 'sharp' ? v : null
}

export function parseSceneDim(v: unknown): SceneDim | null {
  return v === 'light' || v === 'mid' || v === 'dark' ? v : null
}

export function parseSceneUrlMode(v: unknown): 'on' | 'off' | null {
  return v === 'on' || v === 'off' ? v : null
}

export function readUrlSceneMode(search: string): 'on' | 'off' | null {
  try {
    const sp = new URLSearchParams(search)
    const direct = parseSceneUrlMode(sp.get('scene'))
    if (direct) return direct
    let state = sp.get('liff.state')
    if (!state) return null
    if (/%3[fd]/i.test(state)) state = decodeURIComponent(state)
    if (state.startsWith('?')) state = state.slice(1)
    return parseSceneUrlMode(new URLSearchParams(state).get('scene'))
  } catch {
    return null
  }
}

/** ค่าดิบ (object ที่มี enabled/loading/member/order/blur) → SceneConfig · บูลีนที่ไม่ใช่ false = เปิด */
export function normalizeSceneConfig(raw: unknown): SceneConfig {
  const o = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>
  return {
    enabled: o.enabled !== false,
    loading: o.loading !== false,
    member: o.member !== false,
    order: o.order !== false,
    blur: parseSceneBlur(o.blur) ?? 'mid',
    dim: parseSceneDim(o.dim) ?? 'mid',
  }
}

/** อ่าน scene จาก response Firestore REST (fields.scene.mapValue.fields.*) */
export function sceneFromRestDoc(doc: unknown): SceneConfig {
  const f = (doc as { fields?: { scene?: { mapValue?: { fields?: Record<string, { booleanValue?: unknown; stringValue?: unknown }> } } } } | null)
    ?.fields?.scene?.mapValue?.fields
  if (!f) return { ...SCENE_DEFAULT }
  return normalizeSceneConfig({
    enabled: f.enabled?.booleanValue,
    loading: f.loading?.booleanValue,
    member: f.member?.booleanValue,
    order: f.order?.booleanValue,
    blur: f.blur?.stringValue,
    dim: f.dim?.stringValue,
  })
}

/** ค่าที่เก็บใน localStorage (JSON) → SceneConfig · พังหรือไม่มี = ค่าเริ่มต้น */
export function parseStoredScene(raw: unknown): SceneConfig {
  if (typeof raw !== 'string') return { ...SCENE_DEFAULT }
  try {
    return normalizeSceneConfig(JSON.parse(raw))
  } catch {
    return { ...SCENE_DEFAULT }
  }
}

/** ?scene= ชนะค่าจากแอดมิน: off = ปิดทั้งหมด · on = เปิดทุกหน้า (คงความเบลอที่แอดมินตั้ง) */
export function applySceneUrlOverride(config: SceneConfig, urlMode: 'on' | 'off' | null): SceneConfig {
  if (urlMode === 'off') return { ...config, enabled: false }
  if (urlMode === 'on') return { ...config, enabled: true, loading: true, member: true, order: true }
  return config
}

/**
 * ไอคอนแอป (19 ต.ค. 2569) — แอดมินเลือกที่ admin.srikanett.com › จัดการระบบ › ธีมหน้าลูกค้า › "ไอคอนแอปหน้าลูกค้า"
 * Firestore `publicIntegrations/ordercusUi.appIcon` = id สไตล์ · 'default' / ไม่มี / ผิดรูป = ไอคอนเดิมใน index.html (ไม่แตะ <link>)
 * ไฟล์ `public/app-icons/{id}-ordercus.jpg` · ถ้าเพิ่มสไตล์ใหม่ ต้องแก้ฝั่งแอดมินพร้อมกัน (`appIconLogic.ts` APP_ICON_OPTIONS)
 * อ่านพร้อมฉากหลังใน REST ครั้งเดียว (`sceneMode.ts` → `appIcon.ts`) — ไม่เพิ่มการอ่าน Firestore
 */
export type AppIconStyle = 'default' | 'ritual' | 'velvet' | 'smoky' | 'trimetal' | 'aura'

export const APP_ICON_STYLES: readonly AppIconStyle[] = ['default', 'ritual', 'velvet', 'smoky', 'trimetal', 'aura']
export const UI_APP_ICON_STORAGE_KEY = 'oc-icon'

export function parseAppIconStyle(v: unknown): AppIconStyle | null {
  return typeof v === 'string' && (APP_ICON_STYLES as readonly string[]).includes(v) ? (v as AppIconStyle) : null
}

/** อ่าน appIcon จาก response Firestore REST (fields.appIcon.stringValue) — ไม่มี/ผิดรูป = default */
export function appIconFromRestDoc(doc: unknown): AppIconStyle {
  const fields = (doc as { fields?: { appIcon?: { stringValue?: unknown } } } | null)?.fields
  return parseAppIconStyle(fields?.appIcon?.stringValue) ?? 'default'
}

/** path ไฟล์ไอคอน · default = null (คงไอคอนเดิม) */
export function appIconHref(style: AppIconStyle): string | null {
  return style === 'default' ? null : `/app-icons/${style}-ordercus.jpg`
}

/**
 * เอฟเฟกต์หน้าออร์เดอร์ (22 ต.ค. 2569) — แอดมินเปิด/ปิดที่ admin.srikanett.com › จัดการระบบ › ธีมหน้าลูกค้า › "เอฟเฟกต์หน้าออร์เดอร์"
 * Firestore `publicIntegrations/ordercusUi.effects` = { lamp, celebrate, dust }
 *   lamp = Stepper ประทีป (เปลวไฟวิ่งตามเส้น + ขั้นปัจจุบันเรืองแสง)
 *   celebrate = ดอกบัวบาน + คำอวยพรสุ่มตอนชำระเงินสำเร็จ (ครั้งเดียวต่อออร์เดอร์)
 *   dust = ละอองทองลอย
 * ไม่มีค่า/ผิดรูป = เปิดทุกแบบ · บูลีนปิดเฉพาะ false จริง · อ่านพร้อมฉากหลังใน REST ครั้งเดียว (ไม่เพิ่มการอ่าน Firestore)
 * ผล: <html data-fx-{lamp|celebrate|dust}="off"> · ชื่อฟิลด์ต้องตรงฝั่งแอดมิน (`ordercusThemeLogic.ts` EFFECT_KEYS)
 */
export type EffectKey = 'lamp' | 'celebrate' | 'dust'
export type EffectsConfig = Record<EffectKey, boolean>

export const EFFECT_KEYS: readonly EffectKey[] = ['lamp', 'celebrate', 'dust']
export const EFFECTS_DEFAULT: EffectsConfig = { lamp: true, celebrate: true, dust: true }
export const UI_EFFECTS_STORAGE_KEY = 'oc-fx'

export function normalizeEffectsConfig(raw: unknown): EffectsConfig {
  const o = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>
  return { lamp: o.lamp !== false, celebrate: o.celebrate !== false, dust: o.dust !== false }
}

export function effectsFromRestDoc(doc: unknown): EffectsConfig {
  const f = (doc as { fields?: { effects?: { mapValue?: { fields?: Record<string, { booleanValue?: unknown }> } } } } | null)
    ?.fields?.effects?.mapValue?.fields
  if (!f) return { ...EFFECTS_DEFAULT }
  return normalizeEffectsConfig({ lamp: f.lamp?.booleanValue, celebrate: f.celebrate?.booleanValue, dust: f.dust?.booleanValue })
}

export function parseStoredEffects(raw: unknown): EffectsConfig {
  if (typeof raw !== 'string') return { ...EFFECTS_DEFAULT }
  try {
    return normalizeEffectsConfig(JSON.parse(raw))
  } catch {
    return { ...EFFECTS_DEFAULT }
  }
}
